import { useEffect, useState } from 'react';
import { MeetingsBlock } from '../meetings/MeetingsBlock.js';
import { useLocation, useNavigate, useParams } from 'react-router';
import { leadPushBlockers, type SyncOp } from '@wr/shared';
import { ApiError, errorMessage } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { DeleteDialog } from '../../components/DeleteDialog.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Notice } from '../../components/ui.js';
import { LISTS, optionLabel } from '../../lists.js';
import { SCREENS } from '../../modules.js';
import { NAV } from '../../nav.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { fullName } from '../people/data.js';
import { loadLead, type LeadContact, type LeadSheetData } from './data.js';
import { stageName, useStages } from './stages.js';

const str = (v: unknown) => (typeof v === 'string' ? v : '');

export const shortDate = (value: unknown) =>
  typeof value === 'string' && value
    ? new Date(value.length === 10 ? `${value}T12:00:00` : value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

const QUALIFICATION: Record<string, string> = {
  qualified: 'Looks qualified',
  disqualified: 'Not qualified',
};

/** Where a window over People goes back to: the page it was opened from, else the leads list. */
export function usePeopleBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  return {
    from,
    close: () => navigate(from ?? { pathname: '/people/leads', search: location.search }),
    /** Another window of the same lead (Edit, Complete), keeping where to go back. */
    open: (path: string) =>
      navigate({ pathname: path, search: location.search }, { replace: true, state: { from } }),
  };
}

/** Loads a lead for its windows, again after each change this device saves or receives. */
export function useLead(id: string) {
  const { user, refresh } = useSignedIn();
  const { store, counts } = useOffline();
  const [data, setData] = useState<LeadSheetData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    loadLead(id, store, { id: user.id, name: `${user.firstName} ${user.lastName}` })
      .then((loaded) => {
        if (!current) return;
        // A lead created a moment ago may reach the portal only now: a later load clears the error.
        setError(loaded ? null : 'This lead does not exist, or it was deleted.');
        if (loaded) setData(loaded);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) {
          setError(
            err instanceof ApiError && err.status === 404
              ? 'This lead does not exist, or it was deleted.'
              : errorMessage(err),
          );
        }
      });
    return () => {
      current = false;
    };
    // counts: a change saved or sent reloads the lead.
  }, [id, store, user, refresh, counts]);
  return { data, error };
}

/** Who may change and delete this lead: everyone's with `.all`, their own with `.own`. */
export function useLeadRights(lead: Record<string, unknown> | null) {
  const { user, can } = useSignedIn();
  const own =
    !lead || lead.created_by === user.id || lead.owner_user_id === user.id || !lead.created_by;
  return {
    change: can('leads.update.all') || (own && can('leads.update.own')),
    remove: can('leads.delete.all') || (own && can('leads.delete.own')),
  };
}

/**
 * The lead sheet (brief section 6.8), a window over People as in
 * docs/prototype.html: the HubSpot state (or what is missing before it),
 * Call, Email, Run ROI, the Contacts block (primary, Make primary, Remove,
 * + Add a contact), Company, Deal, the Discovery Capture in full, its exit
 * criteria, the linked ROI projects and packages, the scanned card.
 */
export function LeadSheet() {
  const { id = '' } = useParams();
  return <Sheet key={id} id={id} />;
}

function Sheet({ id }: { id: string }) {
  const { user, can } = useSignedIn();
  const { save } = useOffline();
  const toast = useToast();
  const navigate = useNavigate();
  const { close, open } = usePeopleBack();
  const { data, error } = useLead(id);
  const rights = useLeadRights(data?.lead ?? null);
  const stages = useStages();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [cardShown, setCardShown] = useState(true);

  if (error || !data) {
    return (
      <Dialog title="Lead" onClose={close} className="wide">
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }

  const l = data.lead;
  const primary = data.contacts.find((c) => c.is_primary) ?? data.contacts[0];
  const phone = str(primary?.mobile) || str(primary?.office_phone);
  const missing = leadPushBlockers(l);
  const synced = l.hubspot_status === 'synced';
  const title = str(l.company_name) || str(l.deal_name) || 'Lead';
  const createdBy =
    l.created_by === user.id || !l.created_by
      ? `${data.createdByName ?? `${user.firstName} ${user.lastName}`} (you)`
      : (data.createdByName ?? 'a former user');
  const card = data.contacts.find((c) => c.has_card);

  async function link(
    op:
      | { action: 'link'; contactId: string; isPrimary: boolean }
      | { action: 'unlink'; contactId: string },
    message: string,
  ) {
    await save({
      opId: newId(),
      entity: 'lead_contact',
      leadId: id,
      changedAt: new Date().toISOString(),
      ...op,
    } as SyncOp);
    toast(message);
  }

  async function remove(alsoHubSpot: boolean) {
    await save({
      opId: newId(),
      entity: 'lead',
      id,
      action: 'delete',
      changedAt: new Date().toISOString(),
      ...(alsoHubSpot ? { alsoHubSpot: true } : {}),
    });
    toast(`Lead deleted: ${title}`);
    close();
  }

  const openContact = (c: LeadContact) =>
    navigate({ pathname: `/people/contacts/${c.id}` }, { state: { from: `/people/leads/${id}` } });

  return (
    <Dialog
      title={title}
      onClose={close}
      className="wide"
      footer={
        <>
          {rights.remove && (
            <button
              type="button"
              className="btn danger left"
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          )}
          {rights.change && (
            <button type="button" className="btn" onClick={() => open(`/people/leads/${id}/edit`)}>
              Edit
            </button>
          )}
          {data.hubspotUrl && (
            <a className="btn" href={data.hubspotUrl} target="_blank" rel="noopener noreferrer">
              Open in HubSpot
            </a>
          )}
          <button type="button" className="btn" onClick={close}>
            Done
          </button>
        </>
      }
    >
      {data.offline && (
        <Notice kind="info">No connection: this is the copy kept on this device.</Notice>
      )}
      {!synced && missing.length > 0 ? (
        <div className="dup">
          <b>
            {missing.length} thing{missing.length > 1 ? 's' : ''} to document before HubSpot
          </b>
          <span>{missing.map((m) => m.label).join(', ')}.</span>
          {rights.change && (
            <div className="actions" style={{ marginTop: 8 }}>
              {can('todo.voice') && (
                <button
                  type="button"
                  className="btn sm primary"
                  onClick={() =>
                    navigate(`/todo/voice/lead/${id}`, {
                      state: { from: `/people/leads/${id}` },
                    })
                  }
                >
                  <Icon name="mic" />
                  By voice
                </button>
              )}
              <button
                type="button"
                className="btn sm"
                onClick={() => open(`/people/leads/${id}/complete`)}
              >
                <Icon name="edit" />
                By keyboard
              </button>
            </div>
          )}
        </div>
      ) : (
        <HubSpotState lead={l} />
      )}
      <div className="actions">
        <span className="note">
          {[data.eventCode ?? 'No event', shortDate(l.created_at), `captured by ${createdBy}`]
            .filter(Boolean)
            .join(', ')}
        </span>
      </div>
      <div className="quick-contact">
        {phone ? (
          <a className="btn" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
            <Icon name="phone" />
            Call
          </a>
        ) : (
          <span />
        )}
        {str(primary?.email) ? (
          <a className="btn" href={`mailto:${str(primary?.email)}`}>
            <Icon name="mail" />
            Email
          </a>
        ) : (
          <span />
        )}
        {SCREENS.roi && can('roi.use') && (
          <button
            type="button"
            className="btn"
            onClick={() =>
              navigate('/roi/new', {
                state: {
                  link: {
                    kind: 'lead',
                    id,
                    label: str(l.deal_name) || title,
                    sub: str(l.company_name) || null,
                    company: str(l.company_name) || title,
                  },
                },
              })
            }
          >
            <Icon name="roi" />
            Run ROI
          </button>
        )}
      </div>

      <div className="formsec lk-sec">
        <h3>Contacts</h3>
        {data.contacts.map((c) => (
          <div key={c.id} className="lk-ct">
            <button type="button" className="lk-ct-main" onClick={() => openContact(c)}>
              <b>{fullName(c) || str(c.email) || 'Contact'}</b>
              {c.is_primary && (
                <>
                  {' '}
                  <span className="badge b-ok">Primary</span>
                </>
              )}
              <span className="note">{[c.job_title, c.company].filter(Boolean).join(', ')}</span>
              <span className="note">{c.email ?? ''}</span>
            </button>
            {rights.change && (
              <span className="actions">
                {!c.is_primary && (
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() =>
                      void link(
                        { action: 'link', contactId: c.id, isPrimary: true },
                        `${fullName(c) || 'Contact'} is now the primary contact.`,
                      )
                    }
                  >
                    Make primary
                  </button>
                )}
                {data.contacts.length > 1 && (
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() =>
                      void link(
                        { action: 'unlink', contactId: c.id },
                        `${fullName(c) || 'Contact'} removed from this lead.`,
                      )
                    }
                  >
                    Remove
                  </button>
                )}
              </span>
            )}
          </div>
        ))}
        {rights.change && (
          <div className="actions">
            <button
              type="button"
              className="btn sm"
              onClick={() => open(`/people/leads/${id}/add-contact`)}
            >
              + Add a contact
            </button>
          </div>
        )}
      </div>
      <MeetingsBlock
        lead={{ id: l.id, label: str(l.deal_name) || str(l.company_name) || 'Lead' }}
        people={data.contacts.map((c) => ({
          id: c.id,
          label: fullName(c) || str(c.email) || 'Contact',
          company: str(c.company) || null,
        }))}
      />

      <div className="detail-grid">
        <div className="formsec">
          <h3>Company</h3>
          <dl className="kv">
            <dt>Name</dt>
            <dd>{str(l.company_name) || '-'}</dd>
            <dt>Country</dt>
            <dd>{str(l.country) || '-'}</dd>
            <dt>Operating market(s)</dt>
            <dd>{(Array.isArray(l.applications) ? l.applications : []).join(' / ') || '-'}</dd>
          </dl>
        </div>
        <div className="formsec">
          <h3>Deal</h3>
          <dl className="kv">
            <dt>Event ID</dt>
            <dd>{data.eventCode ?? '-'}</dd>
            <dt>Deal name</dt>
            <dd>{str(l.deal_name) || '-'}</dd>
            <dt>Installation country</dt>
            <dd>{str(l.installation_country) || '-'}</dd>
            <dt>Deal owner</dt>
            <dd>{data.ownerName ?? '-'}</dd>
            <dt>Deal stage</dt>
            <dd>{stageName(l.stage_id, stages) || '-'}</dd>
            <dt>Deal market</dt>
            <dd>{str(l.deal_market) || '-'}</dd>
            <dt>Last seen</dt>
            <dd>{data.eventCode ?? '-'}</dd>
            <dt>Pipeline</dt>
            <dd>Sales Pipeline</dd>
            <dt>Note</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{str(l.note) || '-'}</dd>
          </dl>
        </div>
        <div className="formsec">
          <h3>Discovery Capture</h3>
          <dl className="kv">
            <dt>Where from</dt>
            <dd>{str(l.where_from) || (l.event_id ? 'Tradeshow' : 'Email')}</dd>
            <dt>Role in the project</dt>
            <dd>{optionLabel(LISTS.roleOptions, str(l.role)) || '-'}</dd>
            <dt>Pain</dt>
            <dd>{str(l.pain) || '-'}</dd>
            <dt>Product fit</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{str(l.product_fit) || '-'}</dd>
            <dt>Outcome</dt>
            <dd>
              <b>{QUALIFICATION[str(l.qualification)] ?? '-'}</b>
            </dd>
            {l.qualification === 'disqualified' ? (
              <>
                <dt>Why not qualified</dt>
                <dd>{str(l.disqualified_why) || '-'}</dd>
                <dt>When this can change</dt>
                <dd>{str(l.disqualified_when) || '-'}</dd>
              </>
            ) : (
              <>
                <dt>Project Call</dt>
                <dd>
                  {str(l.project_call_date) ? (
                    shortDate(l.project_call_date)
                  ) : (
                    <span className="due-over">Not booked yet</span>
                  )}
                </dd>
              </>
            )}
          </dl>
        </div>
      </div>

      <div className="detail-grid">
        <div className="formsec exitbox">
          <h3>Discovery Capture: exit criteria</h3>
          <ul className="exit">
            <li className={missing.length === 0 ? 'ok' : ''}>
              <span className="xb">{missing.length === 0 ? '✓' : ''}</span>
              <span>Lead documented with the technical details</span>
              <span className="xa">auto</span>
            </li>
            <li className={str(l.project_call_date) ? 'ok' : ''}>
              <span className="xb">{str(l.project_call_date) ? '✓' : ''}</span>
              <span>
                Next meeting booked for the Project Call
                <small>
                  {str(l.project_call_date) ? shortDate(l.project_call_date) : 'Not booked yet'}
                </small>
              </span>
              <span className="xa">auto</span>
            </li>
          </ul>
        </div>
      </div>

      {(data.roi || NAV.packages.allowed(can)) && (
        <div className="detail-grid">
          {data.roi && (
            <div className="formsec">
              <h3>ROI projects</h3>
              {data.roi.length > 0 ? (
                <ul className="linked">
                  {data.roi.map((r) => (
                    <li key={r.id}>
                      <span>
                        {r.scenarioName}
                        <br />
                        <span className="note">
                          {r.payback === null
                            ? 'No payback'
                            : `${r.payback.toFixed(2)} yr payback, ${r.roiYear1 ?? 0}% ROI year 1`}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="btn sm"
                        onClick={() => navigate(`/roi/${r.id}`)}
                      >
                        Open
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="note">None yet.</p>
              )}
            </div>
          )}
          {NAV.packages.allowed(can) && (
            <div className="formsec">
              <h3>Packages</h3>
              <p className="note">None yet.</p>
            </div>
          )}
        </div>
      )}

      {card && cardShown && (
        <button
          type="button"
          className={`zoomimg${zoom ? ' zoom' : ''}`}
          aria-label={zoom ? 'Smaller card image' : 'Larger card image'}
          onClick={() => setZoom((z) => !z)}
        >
          <img
            src={`/api/capture/scans/${encodeURIComponent(card.id)}/card`}
            alt="Scanned card"
            onError={() => setCardShown(false)}
          />
        </button>
      )}

      {confirmDelete && (
        <DeleteDialog
          title="Delete this lead?"
          inHubSpot={Boolean(l.hubspot_id)}
          hubspotLabel="Also delete its deal in HubSpot, and its company if the portal created it and nothing else is on it (they can be restored there for 90 days)."
          onCancel={() => setConfirmDelete(false)}
          onDelete={(alsoHubSpot) => void remove(alsoHubSpot)}
        >
          It will be removed from the portal. Its contacts stay.
        </DeleteDialog>
      )}
    </Dialog>
  );
}

/** The follow-up tasks the push created in HubSpot (brief section 2, rule 12). */
export const HUBSPOT_TASK_LABELS: Record<string, string> = {
  book_project_call: 'Book the Project Call',
  send_content: 'Send brochure and video',
};

export function HubSpotTasks({ record }: { record: Record<string, unknown> }) {
  const tasks = (Array.isArray(record.hubspot_tasks) ? (record.hubspot_tasks as string[]) : []).map(
    (t) => HUBSPOT_TASK_LABELS[t] ?? t,
  );
  return tasks.length > 0 ? (
    <span>
      HubSpot task{tasks.length > 1 ? 's' : ''} created: {tasks.join(', ')}
    </span>
  ) : null;
}

function HubSpotState({ lead }: { lead: Record<string, unknown> }) {
  const { counts } = useOffline();
  if (lead.hubspot_status === 'synced') {
    return (
      <div className="hs-state ok">
        <b>In HubSpot since {shortDate(lead.synced_at)}</b>
        <HubSpotTasks record={lead} />
        <span>Changes made here update HubSpot, and changes made in HubSpot come back here.</span>
        {Boolean(lead.hubspot_pulled_at) && (
          <span>Updated from HubSpot {shortDate(lead.hubspot_pulled_at)}</span>
        )}
      </div>
    );
  }
  if (lead.hubspot_status === 'error') {
    return (
      <div className="hs-state wait">
        <b>HubSpot did not take it yet</b>
        <span>{str(lead.hubspot_error) || 'The portal tries again by itself.'}</span>
      </div>
    );
  }
  if (counts.pending > 0 && typeof navigator !== 'undefined' && !navigator.onLine) {
    return (
      <div className="hs-state wait">
        <b>Waiting for network</b>
        <span>Documented. It goes to HubSpot as soon as the connection is back.</span>
      </div>
    );
  }
  return (
    <div className="hs-state wait">
      <b>Sending to HubSpot…</b>
      {/* A lead waits for its contacts: the push says so on the lead. */}
      {str(lead.hubspot_error) && <span>{str(lead.hubspot_error)}</span>}
    </div>
  );
}
